import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'bq.theme';

function readInitial(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function applyToDom(mode: ThemeMode) {
  const root = document.documentElement;
  if (mode === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', mode);
  }
}

/**
 * Theme mode hook with 3-state cycle (light → dark → system).
 * Persists to localStorage; respects prefers-color-scheme when 'system'.
 * Sets data-theme on <html> for token override (see design-tokens dark block).
 */
export function useTheme(): {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  toggle: () => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(readInitial);

  useEffect(() => {
    applyToDom(mode);
    if (mode === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  return {
    mode,
    setMode: setModeState,
    toggle: () => setModeState((prev) =>
      prev === 'light' ? 'dark' : prev === 'dark' ? 'system' : 'light',
    ),
  };
}
